"use client";

import Script from "next/script";
import { useEffect, useRef, useState } from "react";
import type { CtlBranch } from "@/api/useGetCtlMapApi";
import type {
  KakaoMapInstance,
  KakaoMarker,
  KakaoInfoWindow,
  KakaoGeocoderResult,
} from "../types";
import {
  KAKAO_MAP_DEFAULT_CENTER,
  KAKAO_MAP_DEFAULT_LEVEL,
} from "../constants";
import { buildBranchInfoWindowContent } from "../utils";

interface KakaoMapProps {
  branches: CtlBranch[];
  selectedId: number | null;
  onSelect: (id: number) => void;
}

export default function KakaoMap({ branches, selectedId, onSelect }: KakaoMapProps) {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<KakaoMapInstance | null>(null);
  const markersRef = useRef<{ marker: KakaoMarker; lat: number; lng: number; id: number }[]>([]);
  const infowindowRef = useRef<KakaoInfoWindow | null>(null);
  const [mapReady, setMapReady] = useState(false);

  const initMap = () => {
    window.kakao.maps.load(() => {
      if (!mapRef.current) return;
      const center = new window.kakao.maps.LatLng(
        KAKAO_MAP_DEFAULT_CENTER.lat,
        KAKAO_MAP_DEFAULT_CENTER.lng,
      );
      const map = new window.kakao.maps.Map(mapRef.current, {
        center,
        level: KAKAO_MAP_DEFAULT_LEVEL,
      });
      map.setMaxLevel(KAKAO_MAP_DEFAULT_LEVEL);
      mapInstanceRef.current = map;
      infowindowRef.current = new window.kakao.maps.InfoWindow({ zIndex: 1 });
      setMapReady(true);
    });
  };

  useEffect(() => {
    if (typeof window !== "undefined" && window.kakao) {
      initMap();
    }
  }, []);

  useEffect(() => {
    if (!mapReady || !mapInstanceRef.current) return;
    const { kakao } = window;

    markersRef.current.forEach(({ marker }) => marker.setMap(null));
    markersRef.current = [];

    const geocoder = new kakao.maps.services.Geocoder();

    const placeMarker = (branch: CtlBranch, lat: number, lng: number) => {
      const position = new kakao.maps.LatLng(lat, lng);
      const marker = new kakao.maps.Marker({ position, map: mapInstanceRef.current! });

      kakao.maps.event.addListener(marker, "click", () => {
        infowindowRef.current?.setContent(buildBranchInfoWindowContent(branch));
        infowindowRef.current?.open(mapInstanceRef.current!, marker);
        onSelect(branch.id);
      });

      markersRef.current.push({ marker, lat, lng, id: branch.id });
    };

    branches.forEach((branch) => {
      geocoder.addressSearch(branch.address, (result: KakaoGeocoderResult[], status: string) => {
        if (status === kakao.maps.services.Status.OK && result.length > 0) {
          placeMarker(branch, Number(result[0].y), Number(result[0].x));
        } else if (branch.latitude && branch.longitude) {
          placeMarker(branch, branch.latitude, branch.longitude);
        }
      });
    });
  }, [mapReady, branches, onSelect]);

  // 테이블 행 클릭 시 해당 마커 위치로 이동
  useEffect(() => {
    if (!mapReady || selectedId === null || !mapInstanceRef.current) return;

    const found = markersRef.current.find((m) => m.id === selectedId);
    if (!found) return;

    const selectedBranch = branches.find((branch) => branch.id === selectedId);
    const position = new window.kakao.maps.LatLng(found.lat, found.lng);
    mapInstanceRef.current.panTo(position);

    if (selectedBranch) {
      infowindowRef.current?.setContent(
        buildBranchInfoWindowContent(selectedBranch),
      );
      infowindowRef.current?.open(mapInstanceRef.current, found.marker);
    }
  }, [selectedId, mapReady, branches]);

  return (
    <div className="relative w-full h-full">
      <Script
        src={`https://dapi.kakao.com/v2/maps/sdk.js?appkey=${process.env.NEXT_PUBLIC_KAKAO_MAP_APP_KEY}&autoload=false&libraries=services`}
        strategy="afterInteractive"
        onLoad={initMap}
      />
      <div ref={mapRef} className="w-full h-full" />
    </div>
  );
}
